import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const DEVICE_KEY = 'hypnose_device_id';

export function getDeviceId() {
  if (typeof window === 'undefined') return '';
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `dev_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

/**
 * useAffirmationClips — fetches all affirmation clips for a theme:
 *  - global voices (ludovic, homme, femme) shared everywhere
 *  - user voice (mon-choix) scoped to local device_id
 * Map key: `${index}-${voice}`.
 */
export default function useAffirmationClips(themeId) {
  const [clips, setClips] = useState({});
  const deviceId = getDeviceId();

  const refresh = useCallback(async () => {
    if (!themeId) return;
    try {
      const r = await axios.get(`${API}/api/affirmation-clips/${themeId}`, { params: { device_id: deviceId } });
      const map = {};
      (r.data || []).forEach((c) => {
        // Prefer the resilient /audio/{id} URL when available (DB-backed, deploy-safe).
        // Fall back to the legacy /api/uploads/... static path otherwise.
        const playUrl = c.audio_url || c.url;
        map[`${c.affirmation_index}-${c.voice}`] = { ...c, url: playUrl };
      });
      setClips(map);
    } catch (e) {
      console.warn('[useAffirmationClips] refresh failed:', e);
      setClips({});
    }
  }, [themeId, deviceId]);

  useEffect(() => { refresh(); }, [refresh]);

  const getClip = (idx, voice) => clips[`${idx}-${voice}`];

  const uploadBlob = async (idx, voice, blob, suggestedName) => {
    const fd = new FormData();
    const filename = suggestedName || `clip_${voice}_${idx}.mp3`;
    fd.append('file', blob, filename);
    if (voice === 'mon-choix') fd.append('device_id', deviceId);
    await axios.post(`${API}/api/affirmation-clips/${themeId}/${idx}/${voice}/upload`, fd);
    await refresh();
  };

  const upload = async (idx, voice, file) => {
    return uploadBlob(idx, voice, file, file.name);
  };

  const remove = async (idx, voice) => {
    await axios.delete(`${API}/api/affirmation-clips/${themeId}/${idx}/${voice}`, {
      params: voice === 'mon-choix' ? { device_id: deviceId } : {},
    });
    await refresh();
  };

  const toggleLock = async (idx, voice) => {
    await axios.put(`${API}/api/affirmation-clips/${themeId}/${idx}/${voice}/lock`, null, {
      params: voice === 'mon-choix' ? { device_id: deviceId } : {},
    });
    await refresh();
  };

  return { clips, deviceId, getClip, upload, uploadBlob, remove, toggleLock, refresh };
}
